import DocumentEditor from "./DocumentEditor.component";
import React, {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {DETAILS_VIEW_TYPE, LOADING_STATUS} from "utils/constants.js";
import {snowplowTrackEvent} from "utils/snowplow.js";
import * as reduxSelectors from "../../../store/application.reducers";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Button, ButtonType} from "components/common/Button.component";
import {ArrowDownToLine, ArrowLeftToLineIcon} from "lucide-react";
import {ICON_SIZE} from "components/common/constants";
import {launchDarkly} from "utils/launchDarkly";
import BnxtDocumentEditor from "./bnxt/BnxtDocumentEditor.component";
import {BNXT_APPLICATION_NAME} from "./bnxt/bnxtOpenTask";


export const DocumentEditorPanel = (props) => {

    const userConfiguration = useSelector((store) => reduxSelectors.getUsersData(store));
    const navigate = useNavigate();
    const [bnxtHandshakeFailed, setBnxtHandshakeFailed] = useState(false);
    const onBnxtHandshakeFailed = useCallback(() => setBnxtHandshakeFailed(true), []);

    // a failure says nothing about the next task, so the postMessage editor gets another go
    useEffect(() => setBnxtHandshakeFailed(false), [props.params.taskId]);
    const getEditorExternalApplicationName = () => {
        switch (props.type) {
            case DETAILS_VIEW_TYPE.TASK:
                return props.task.externalApplicationName;
            case DETAILS_VIEW_TYPE.HISTORY_TASK:
            case DETAILS_VIEW_TYPE.PROCESS:
                return props.task.applicationTypeName;
            case DETAILS_VIEW_TYPE.DOCUMENT:
                return props.task.applicationTypeName;
            default:
                return props.task.externalApplicationName;
        }
    }

    const getDocumentType = () => {
        return props.task.documentType;
    }

    const getEditorExternalId = () => {
        switch (props.type) {
            case DETAILS_VIEW_TYPE.TASK:
                return props.task.externalId;
            case DETAILS_VIEW_TYPE.HISTORY_TASK:
            case DETAILS_VIEW_TYPE.PROCESS:
                return props.task.externalApplicationId;
            case DETAILS_VIEW_TYPE.DOCUMENT:
                return props.task.claimId;
            default:
                return props.task.externalId;
        }
    }

    const trackPanelCollapsing = (isCollapsed) => {
        snowplowTrackEvent({
            category: "external_editor_panel",
            action: "external_editor_collapsing",
            label: "external_editor_panel",
            value: isCollapsed ? "collapsed" : "extended"
        }, userConfiguration);
    }

    const applicationName = props.task.externalApplicationName || props.task.applicationTypeName || '';
    const externalApplicationNameKey = applicationName.replace(/[ .]/g, '');
    const isCompelloTask = !props.task?.taskActions?.includes('postpone');
    const useBnxtOpenTaskMessage = getEditorExternalApplicationName() === BNXT_APPLICATION_NAME
        && launchDarkly.userClient?.variation("fe-bnxt-open-task-message", false)
        && !bnxtHandshakeFailed;
    const EditorComponent = useBnxtOpenTaskMessage ? BnxtDocumentEditor : DocumentEditor;
    // the details keep the previous task while the next one loads, and its id would go out in a second openTask
    const bnxtProps = useBnxtOpenTaskMessage
        ? {onHandshakeFailed: onBnxtHandshakeFailed, detailsLoaded: props.loadingStatus !== LOADING_STATUS.LOADING}
        : {};

    if (props.taskDetails.externalEditorAvailable) {
        return (
            <div className={`grid-layout-editor ${props.renderInPortal ? 'in-portal' : ''}`}
                 data-cy="taskDetail.externalEditorPanel">
                <div className="grid-panel-wrapper">
                    <div className="grid-panel">
                        <div className="grid-panel-header d-flex justify-content-between align-items-center">
                            <strong>
                                {props.translate("taskDetail.externalEditor")} {props.translationKeyExists(`taskDetail.applicationName.${externalApplicationNameKey}`) ?
                                props.translate(`taskDetail.applicationName.${externalApplicationNameKey}`) : applicationName}
                            </strong>
                            {!props.inDetachedWindow && (
                                <div className="d-flex gap-8">
                                    <Button type={ButtonType.GHOST}
                                            onClick={() => {
                                                trackPanelCollapsing(true);
                                                props.onRenderInPortalChange(true);
                                            }}
                                            disabled={props.renderInPortal}
                                            ariaLabel={props.translate("taskDetail.placeAtTheSide")}
                                            iconOnly={true}
                                            icon={<ArrowLeftToLineIcon size={ICON_SIZE.MEDIUM}/>}
                                            tooltip={{id: "editor-place-side-tooltip", content: () => props.translate("taskDetail.placeAtTheSide")}}/>
                                    <Button type={ButtonType.GHOST}
                                            onClick={() => {
                                                trackPanelCollapsing(false);
                                                props.onRenderInPortalChange(false);
                                            }}
                                            disabled={!props.renderInPortal}
                                            ariaLabel={props.translate("taskDetail.placeAtTheBottom")}
                                            iconOnly={true}
                                            icon={<ArrowDownToLine size={ICON_SIZE.MEDIUM}/>}
                                            tooltip={{id: "editor-place-bottom-tooltip", content: () => props.translate("taskDetail.placeAtTheBottom")}}/>
                                </div>
                            )}

                        </div>
                        <div className={`grid-editor ${props.renderInPortal ? "" : "grid-editor-large"}`}>
                            <EditorComponent {...bnxtProps}
                                             translate={props.translate}
                                             taskId={props.params.taskId}
                                             uid={props.params.uid}
                                             id={props.taskDetails ? props.taskDetails.id : null}
                                             isCompelloTask={isCompelloTask}
                                             isHistory={props.isHistory}
                                             isReadOnly={props.editorReadOnly || props.taskDetails.readonly}
                                             externalAppId={getEditorExternalId()}
                                             externalApplicationName={getEditorExternalApplicationName()}
                                             documentType={getDocumentType()}
                                             companyId={props.task.companyId}
                                             reapproveType={props.taskDetails ? props.taskDetails.reapproveType : null}
                                             route={navigate}
                                             viewType={props.type}
                                             isPopup={false}/>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        return null;
    }

}

DocumentEditorPanel.propTypes = {
    params: PropTypes.object, // route params, read for the task id
    task: PropTypes.object,
    taskDetails: PropTypes.object,
    type: PropTypes.string, // one of DETAILS_VIEW_TYPE
    isHistory: PropTypes.bool,
    loadingStatus: PropTypes.number,
    editorReadOnly: PropTypes.bool,
    renderInPortal: PropTypes.bool,
    inDetachedWindow: PropTypes.bool,
    onRenderInPortalChange: PropTypes.func,
    translate: PropTypes.func,
    translationKeyExists: PropTypes.func
};

export default DocumentEditorPanel;